import { useEffect, useState, type ReactNode } from 'react'
import { MarketingPageShell } from '@/components/landing/marketing-page-shell'
import { YeshumanBrandMark } from '@/components/landing/yeshuman/yeshuman-brand-mark'
import { YeshumanContactSheetProvider } from '@/components/landing/yeshuman/yeshuman-contact-sheet-provider'
import { YeshumanMarketingFooter } from '@/components/landing/yeshuman/yeshuman-marketing-footer'
import { useGuestSurfaceTheme } from '@/hooks/use-guest-surface-theme'
import { marketingContentColumnStyle } from '@/landing/marketing-layout-tokens'
import { resolveLanding, type ClientWithLanding } from '@/landing/resolve-landing'
import { cn } from '@/lib/utils'
import { CURRENT_CLIENT } from '@/constants'
import './yeshuman-workbench.css'

export function YeshumanMarketingChrome({
  children,
  stickyHeaderSolid = true,
  stickyHeaderContentInset = false,
}: {
  children: ReactNode
  stickyHeaderSolid?: boolean
  stickyHeaderContentInset?: boolean
}) {
  const { guestTheme, guestPreference, setGuestTheme } = useGuestSurfaceTheme()
  const client = CURRENT_CLIENT as ClientWithLanding
  const { headerAuth, navLinks, mobileNavCta, chrome } = resolveLanding(client, guestTheme)
  const contentColumnStyle = marketingContentColumnStyle(chrome.contentMaxWidth)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      setLoaded(true)
      return
    }
    const id = requestAnimationFrame(() => setLoaded(true))
    return () => cancelAnimationFrame(id)
  }, [])

  return (
    <YeshumanContactSheetProvider>
      <MarketingPageShell
        shellClassName="yeshuman-marketing-shell"
        stickyOverlayHeader
        stickyHeaderSolid={stickyHeaderSolid}
        stickyHeaderContentInset={stickyHeaderContentInset}
        transparentShell={false}
        signInLabel={headerAuth.signInLabel}
        registerLabel={headerAuth.registerLabel}
        hideRegister={headerAuth.hideRegister}
        hideSignIn={headerAuth.hideSignIn}
        guestTheme={guestTheme}
        guestPreference={guestPreference}
        onGuestThemeChange={setGuestTheme}
        navLinks={navLinks}
        mobileNavCta={mobileNavCta}
        headerBrand={<YeshumanBrandMark className="[&_svg]:size-12 [&_svg]:shrink-0" />}
        headerSurfaceVariant="cream"
      >
        <div
          className={cn(
            'yeshuman-workbench text-base leading-[1.55] text-[var(--marketing-text)] antialiased',
            'bg-[var(--marketing-bg)] selection:bg-[var(--marketing-gold)] selection:text-[var(--marketing-bg)]',
            loaded && 'yeshuman-loaded',
          )}
          style={contentColumnStyle}
        >
          <a href="#main" className="yeshuman-skip-link">
            Skip to main content
          </a>
          {children}
          <YeshumanMarketingFooter />
        </div>
      </MarketingPageShell>
    </YeshumanContactSheetProvider>
  )
}
